import React, { useEffect } from "react";
import { useTranslations } from "src/contexts/TranslationContext";
import CommonPage from "../components/CommonPage";

interface AboutPageProps {
  lang?: string;
}

const About: React.FC<AboutPageProps> = ({ lang: propLang }) => {
  const { translations: t, language, changeLanguage, isLoading } = useTranslations();

  useEffect(() => {
    if (propLang && propLang !== language) {
      changeLanguage(propLang);
    }
  }, [propLang, language, changeLanguage]);

  // Add logging to help diagnose translation issues
  useEffect(() => {
    console.log('About Component - Available translations:', {
      hasAbout: !!t?.navigations?.about,
      description: t?.navigations?.about?.description || 'Not available',
      language,
      isLoading,
      rootKeys: t ? Object.keys(t) : []
    });
    
    if (t?.navigations?.about) {
      console.log('About structure:', Object.keys(t.navigations.about));
    }
  }, [t, language, isLoading]);

  // Add a timeout to prevent infinite loading
  const [timeoutReached, setTimeoutReached] = React.useState(false);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        console.log('About page - Loading timeout reached');
        setTimeoutReached(true);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show loading state if translations are still loading
  if (isLoading && !timeoutReached) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">Loading About Page...</h2>
        <p className="text-gray-500">Please wait while we load the content in {language}...</p>
      </div>
    );
  }

  const content = (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-8">
        <div className="container mx-auto px-4 max-w-4xl lg:max-w-5xl">
          <p className="text-lg md:text-xl text-center mx-auto leading-relaxed text-gray-700 max-w-3xl">
            <span className="font-semibold">Global 24</span> {t?.navigations?.about?.description || 'is your trusted source for real-time global news, delivering breaking stories with unmatched speed and accuracy.'}
          </p>
          <div className="flex justify-center mt-6">
            <a href="/" className="block w-full max-w-3xl">
              <img
                src="/favicon.ico"
                alt="Home"
                className="w-full h-auto mx-auto cursor-pointer"
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </a>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl lg:max-w-5xl">
        {/* Value propositions - Horizontal slides on larger screens */}
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Slide 1 */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex items-center mb-3">
                  <div className="text-2xl text-blue-600 mr-3">⚡</div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t?.navigations?.about?.realTimeTitle ? t.navigations.about.realTimeTitle.replace('⚡ ', '') : 'Real-Time Breaking News'}
                  </h2>
                </div>
                <p className="text-gray-700 text-base leading-relaxed">
                  {t?.navigations?.about?.realTimeContent || 'We specialize in real-time news delivery, bringing you updates as events unfold.'}
                </p>
              </div>
            </div>

            {/* Slide 2 */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex items-center mb-3">
                  <div className="text-2xl text-blue-600 mr-3">📰</div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t?.navigations?.about?.unbiasedTitle ? t.navigations.about.unbiasedTitle.replace('📰 ', '') : 'Unbiased News Coverage'}
                  </h2>
                </div>
                <p className="text-gray-700 text-base leading-relaxed">
                  {t?.navigations?.about?.unbiasedContent || 'Our editorial team is committed to journalistic integrity, delivering stories without political bias or agenda.'}
                </p>
              </div>
            </div>

            {/* Slide 3 */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex items-center mb-3">
                  <div className="text-2xl text-blue-600 mr-3">✅</div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t?.navigations?.about?.accurateTitle ? t.navigations.about.accurateTitle.replace('✅ ', '') : 'Most Accurate Global News'}
                  </h2>
                </div>
                <p className="text-gray-700 text-base leading-relaxed">
                  {t?.navigations?.about?.accurateContent || 'We combine AI-powered verification with expert editorial oversight to ensure high accuracy.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Coverage section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-10 border border-gray-200">
          <div className="flex items-center mb-5">
            <div className="text-2xl text-blue-600 mr-3">🌐</div>
            <h2 className="text-xl font-bold text-gray-900">
              {t?.navigations?.about?.coverageTitle ? t.navigations.about.coverageTitle.replace('🌐 ', '') : 'Global Coverage'}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {t?.navigations?.about?.coverageList?.map((item: string, index: number) => (
              <div key={index} className="flex items-center p-3 hover:bg-blue-50 rounded-md transition-colors">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-3 flex-shrink-0"></div>
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Archive section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-10 border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="text-2xl text-blue-600 mr-3">🗂️</div>
            <h2 className="text-xl font-bold text-gray-900">
              {t?.navigations?.about?.archiveTitle ? t.navigations.about.archiveTitle.replace('🗂️ ', '') : 'News Archive Access'}
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mt-2">
            {t?.navigations?.about?.archiveContent || 'Our searchable news archive offers deep context on historical events, timelines, and in-depth investigations.'}
          </p>
        </div>

        {/* Why Choose section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-10 border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="text-2xl text-blue-600 mr-3">🌟</div>
            <h2 className="text-xl font-bold text-gray-900">
              {t?.navigations?.about?.whyChooseTitle ? t.navigations.about.whyChooseTitle.replace('🌟 ', '') : 'Why Choose Global 24?'}
            </h2>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {t?.navigations?.about?.whyChooseList?.map((item: string, index: number) => (
              <div key={index} className="flex items-start p-3 rounded-lg bg-gray-50">
                <div className="w-5 h-5 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 font-bold mr-3 flex-shrink-0 text-sm">
                  {index + 1}
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Conclusion */}
        <div className="text-center p-6 mb-8">
          <p className="text-lg font-medium mx-auto text-gray-700 leading-relaxed max-w-2xl">
            {t?.navigations?.about?.conclusion || 'Global 24 is where speed meets credibility. Join millions of readers who choose truth over trends.'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <CommonPage
      lang={propLang}
      pageType="about"
      title={t?.navigations?.about?.title || "About Global 24"}
      content={content}
    />
  );
};

export default About;
